import type { SearchFilters, SortField, SortDirection } from "@/types/job-search"
import type { ATSCandidate } from "@/lib/mock-data"
import {
  extractSearchFiltersDraft,
  FilterFormData,
  SearchFiltersDraft,
  simpleFiltersToDraft,
  type FilterLocationFullItem,
  type SimpleFilterData,
} from "@/lib/search-filter-model"
import { GetSearch200Item, GetSearchQueries200 } from "openapi/api/model"

export const filterCandidates = (candidates: ATSCandidate[], filters: SearchFilters): ATSCandidate[] => {
  return candidates.filter((candidate) => {
    const searchTerm = filters.searchTerm?.toLowerCase() || ""

    const matchesSearch =
      !searchTerm ||
      candidate.full_name?.toLowerCase().includes(searchTerm) ||
      candidate.headline?.toLowerCase().includes(searchTerm) ||
      candidate.experience?.[0]?.company_name?.toLowerCase().includes(searchTerm) ||
      candidate.inferred_skills?.some((skill) => skill?.toLowerCase().includes(searchTerm))

    const matchesStage = filters.stageFilter === "all" || candidate.pipeline_stage === filters.stageFilter
    const matchesLocation = filters.locationFilter === "all" || candidate.location_country === filters.locationFilter
    const matchesScore =
      filters.matchScoreFilter === "all" ||
      (filters.matchScoreFilter === "excellent" && candidate.match_score >= 90) ||
      (filters.matchScoreFilter === "good" && candidate.match_score >= 80 && candidate.match_score < 90) ||
      (filters.matchScoreFilter === "fair" && candidate.match_score < 80)

    return matchesSearch && matchesStage && matchesLocation && matchesScore
  })
}

export const sortCandidates = (
  candidates: ATSCandidate[],
  sortField: SortField,
  sortDirection: SortDirection,
): ATSCandidate[] => {
  return [...candidates].sort((a, b) => {
    let aValue: any, bValue: any

    switch (sortField) {
      case "name":
        aValue = a.full_name
        bValue = b.full_name
        break
      case "match_score":
        aValue = a.match_score
        bValue = b.match_score
        break
      case "experience":
        aValue = a.total_experience_duration_months
        bValue = b.total_experience_duration_months
        break
      case "salary":
        aValue = a.projected_total_salary_median
        bValue = b.projected_total_salary_median
        break
      case "added_date":
        aValue = new Date(a.added_date)
        bValue = new Date(b.added_date)
        break
      case "last_contact":
        aValue = a.last_contact ? new Date(a.last_contact) : new Date(0)
        bValue = b.last_contact ? new Date(b.last_contact) : new Date(0)
        break
      default:
        aValue = a.match_score
        bValue = b.match_score
    }

    if (sortDirection === "asc") {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  })
}

export const getStageStats = (candidates: ATSCandidate[]) => {
  return {
    longlist: candidates.filter((c) => c.pipeline_stage === "longlist").length,
    shortlist: candidates.filter((c) => c.pipeline_stage === "shortlist").length,
    interviewing: candidates.filter((c) => c.pipeline_stage === "interviewing").length,
  }
}


interface NormalizedFilters {
  minExperience: string;
  maxExperience: string;
  jobTitles: string[];
  companies: string[];
  companyIndustries: string[];
  companyTags: string;
  skills: string[];
  locationCountry:string
  locationRegion:string[]
  locationCity:string[];
  excludedCompanies:string[]
  tenure:string[]
  org_sizes:string[],
  seniority:string[],
  current_titles_only:boolean,
  keywords:string[],
  location_full: FilterLocationFullItem[]
}

const toNonEmptyList = (values: unknown[] = []): string[] =>
  values
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter((value): value is string => Boolean(value))


export const searchAdaptor=(rawSearches:GetSearch200Item[] | undefined)=>{
  return rawSearches?.map((searchObj)=>{
    return{
      createdAt:searchObj.created_at,
      filters:{
        experience:'',
        jobType:'Full time',
        location:searchObj.city?searchObj.city : searchObj.country,
        skills:searchObj.skills?.split(" OR ")??[],
      },
      id:searchObj.id,
      lastUsed:searchObj.created_at,
      name:searchObj.prompt??'',
      resultsCount:searchObj.total_result_count,
      salary:{
        min:'',
        max:''
      },
      hash:searchObj.normalized_query_hash??''
    }
  })
}


interface SavedSearch {
  id: string
  name: string
  query: string
  filters: string[]
  createdAt: string
}


/**
 * Utility to truncate long text safely.
 */
 function truncate(text: string, maxLength = 40): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength).trimEnd() + "..."
}

/**
 * Converts API search objects into simplified SavedSearch summaries
 */
export function mapToSavedSearches(items: GetSearch200Item[]): SavedSearch[] {
  return items.map((item) => {
    const id = item.id as string

    // 1️⃣ Pick a base title
    const baseTitle =
      item.current_role_title ||
      item.prompt ||
      item.keyword_query ||
      "Untitled Search"

    // 2️⃣ Clean and shorten the name for display
    const name = truncate(
      baseTitle
        .replace(/\b(OR|AND)\b/gi, "/")
        .replace(/\s+/g, " ")
        .trim()
    )

    // 3️⃣ Filters derived from multiple useful fields
    const filters: string[] = [
      ...(item.skills
        ? item.skills
            .split(/\bOR\b|\bAND\b/gi)
            .map((s) => s.trim())
            .filter(Boolean)
        : []),
      ...(item.city ? [item.city] : []),
      ...(item.region ? [item.region] : []),
      ...(item.country ? [item.country] : []),
    ].slice(0,5)

    // 4️⃣ Truncate prompt for summary view
    const query = item.prompt ? truncate(item.prompt, 120) : truncate(baseTitle)

    // 5️⃣ Format date
    const createdAt = item.created_at
      ? new Date(item.created_at).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0]

    return {
      id,
      name,
      query,
      filters,
      createdAt,
    }
  })
}

/** Utility: safely parse ISO dates for sorting */
function parseDate(dateString?: string | null): number {
  const d = dateString ? new Date(dateString) : new Date(0)
  return isNaN(d.getTime()) ? 0 : d.getTime()
}

/** Sorter: Newest first (most recent createdAt at top) */
export function sortSavedSearchesByDate(searches: SavedSearch[]): SavedSearch[] {
  return [...searches].sort(
    (a, b) => parseDate(b.createdAt) - parseDate(a.createdAt)
  )
}

export const mapLocationFullToPayload = (locations: FilterLocationFullItem[] = []): FilterLocationFullItem[] => {
  return locations
    .map((location) => ({
      city: location.city?.trim() || null,
      region: location.region?.trim() || null,
      country: location.country ?? null,
      label:
        location.label?.trim() ||
        [location.city?.trim(), location.region?.trim()].filter(Boolean).join(", ") ||
        location.city?.trim() ||
        location.region?.trim() ||
        "",
    }))
    .filter((location) => Boolean(location.city || location.region || location.country || location.label))
}

export const mapSearchFiltersToSearchPayload = (filters: SearchFiltersDraft): Partial<NormalizedFilters> => {
  const locations = toNonEmptyList(filters.locations)
  const locationRegions = toNonEmptyList(filters.locationRegion)
  const jobTitles = toNonEmptyList(filters.jobTitles)
  const companies = toNonEmptyList(filters.companies)
  const companyIndustries = toNonEmptyList(filters.companyIndustries)
  const skills = toNonEmptyList(filters.skills)
  const excludedCompanies = toNonEmptyList(filters.excludedCompanies)
  const tenure = toNonEmptyList(filters.timeSpentMin)
  const companySizes = toNonEmptyList(filters.companySizes)
  const seniority = toNonEmptyList(filters.jobTitleLevels)
  const keywords = toNonEmptyList(filters.keywords)

  const payload: Partial<NormalizedFilters> = {
    ...(filters.minExperience ? { minExperience: filters.minExperience } : {}),
    ...(filters.maxExperience ? { maxExperience: filters.maxExperience } : {}),
    ...(locations.length > 0 ? { locationCity: locations } : {}),
    ...(locationRegions.length > 0 ? { locationRegion: locationRegions } : {}),
    ...(filters.locationCountry ? { locationCountry: filters.locationCountry } : {}),
    ...(jobTitles.length > 0 ? { jobTitles } : {}),
    ...(companies.length > 0 ? { companies } : {}),
    ...(companyIndustries.length > 0 ? { companyIndustries } : {}),
    ...(skills.length > 0 ? { skills } : {}),
    ...(excludedCompanies.length > 0 ? { excludedCompanies } : {}),
    ...(tenure.length > 0 ? { tenure } : {}),
    ...(companySizes.length > 0 ? { org_sizes: companySizes } : {}),
    ...(seniority.length > 0 ? { seniority } : {}),
    ...(keywords.length > 0 ? { keywords } : {}),
    current_titles_only: filters.currentOnly !== "current-past",
  }

  const locationFull = mapLocationFullToPayload(filters.locationFull)

  if (locationFull.length > 0) {
    payload.location_full = locationFull
  }

  if (!seniority.length) {
    delete payload.seniority
  }

  return payload
}

export const mapSearchConfigToFiltersDraft = (searchConfig?: GetSearchQueries200): SearchFiltersDraft => {
  const draftFromUiFilters = simpleFiltersToDraft((searchConfig?.ui_filters ?? {}) as unknown as Partial<SimpleFilterData>)

  return {
    ...draftFromUiFilters,
    jobTitles: draftFromUiFilters.jobTitles.length > 0 ? draftFromUiFilters.jobTitles : searchConfig?.role ?? [],
    locations: draftFromUiFilters.locations.length > 0 ? draftFromUiFilters.locations : searchConfig?.locationCity ?? [],
    locationCountry: draftFromUiFilters.locationCountry || searchConfig?.locationCountry || "",
    companyIndustries:
      draftFromUiFilters.companyIndustries.length > 0
        ? draftFromUiFilters.companyIndustries
        : searchConfig?.industries ?? [],
    companies: draftFromUiFilters.companies.length > 0 ? draftFromUiFilters.companies : searchConfig?.companies ?? [],
    skills: draftFromUiFilters.skills.length > 0 ? draftFromUiFilters.skills : searchConfig?.skills ?? [],
    jobTitleLevels:
      draftFromUiFilters.jobTitleLevels.length > 0
        ? draftFromUiFilters.jobTitleLevels
        : searchConfig?.seniority
          ? [searchConfig.seniority]
          : [],
  }
}

export const mapFilterFormDataToSearchPayload = (data: FilterFormData): Partial<NormalizedFilters> => {
  const payload = mapSearchFiltersToSearchPayload(extractSearchFiltersDraft(data))
  const locationFull = mapLocationFullToPayload(data.locationFull)

  if (locationFull.length > 0) {
    payload.location_full = locationFull
  }

  return payload
}
